import React, { useMemo, useState } from "react";
import { Button, Card, Col, Row, Space, Typography, message } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";

import RateFilters from "../../components/rate/RateFilters";
import RateTable from "../../components/rate/RateTable";
import RateAddModal from "../../components/rate/RateAddModal";
import RateEditModal from "../../components/rate/RateEditModal";
import { getRateAccess } from "../../components/rate/ratePermissions";
import useRateList from "../../hooks/useRateList";
import { deleteRate } from "../../api/rateApi";
import { useTranslation } from "../../i18n/LanguageProvider";

const { Title } = Typography;

/**
 * Ханш page: the module's single destination. Listing, searching, filtering, sorting and - for
 * the add roles - Excel import and manual entry all live here.
 */
function RateListPage() {
    const { t } = useTranslation();
    const access = useMemo(() => getRateAccess(), []);

    const {
        rows,
        loading,
        error,
        page,
        pageSize,
        total,
        search,
        setSearch,
        columnFilters,
        executedDateRange,
        setExecutedDateRange,
        filterOptions,
        handleTableChange,
        clearFilters,
        reload,
    } = useRateList();

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);

    const openEditModal = (record) => {
        setEditRecord(record);
        setEditModalOpen(true);
    };

    const closeEditModal = () => {
        setEditModalOpen(false);
        setEditRecord(null);
    };

    const handleDelete = async (record) => {
        if (!access.canDelete) {
            message.error(t("delete.noPermission"));
            return;
        }

        try {
            await deleteRate(record.id);
            message.success(t("rate.delete.done"));
            reload();
        } catch (err) {
            message.error(err?.message || t("rate.delete.failed"));
        }
    };

    const pagination = {
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100],
        showTotal: (count, range) => `${range[0]}-${range[1]} / ${count}`,
    };

    return (
        <div className="space-y-4">
            <Card bordered={false} className="shadow-sm rounded-2xl">
                <Row gutter={[16, 16]} align="middle" justify="space-between">
                    <Col xs={24} md={14}>
                        <Title level={2} style={{ margin: 0 }}>
                            {t("rate.title")}
                        </Title>
                    </Col>

                    <Col xs={24} md="auto">
                        <Space wrap>
                            {access.canAdd && (
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setAddModalOpen(true)}
                                >
                                    {t("action.add")}
                                </Button>
                            )}

                            <Button icon={<ReloadOutlined />} onClick={reload} loading={loading}>
                                {t("action.refresh")}
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} xl={12}>
                    <Card bordered={false} className="shadow-sm rounded-2xl">
                        <div>
                            <strong>{t("rate.stat.total")}:</strong> {filterOptions.totalCount}
                        </div>
                    </Card>
                </Col>
                <Col xs={24} sm={12} xl={12}>
                    <Card bordered={false} className="shadow-sm rounded-2xl">
                        <div>
                            <strong>{t("rate.stat.filtered")}:</strong> {total}
                        </div>
                    </Card>
                </Col>
            </Row>

            <RateFilters
                search={search}
                setSearch={setSearch}
                executedDateRange={executedDateRange}
                setExecutedDateRange={setExecutedDateRange}
                columnFilterCount={Object.keys(columnFilters || {}).length}
                totalRows={total}
                onClear={clearFilters}
            />

            <Card bordered={false} className="shadow-sm rounded-2xl">
                <RateTable
                    rows={rows}
                    loading={loading}
                    error={error}
                    pagination={pagination}
                    onChange={handleTableChange}
                    currencyOptions={filterOptions.currencies}
                    institutionOptions={filterOptions.institutions}
                    activeFilters={columnFilters}
                    canEdit={access.canEdit}
                    canDelete={access.canDelete}
                    onEdit={openEditModal}
                    onDelete={handleDelete}
                    onReload={reload}
                />
            </Card>

            {access.canAdd && (
                <RateAddModal
                    open={addModalOpen}
                    institutionOptions={filterOptions.institutions}
                    onClose={() => setAddModalOpen(false)}
                    onSaved={reload}
                />
            )}

            {access.canEdit && (
                <RateEditModal
                    open={editModalOpen}
                    record={editRecord}
                    institutionOptions={filterOptions.institutions}
                    onClose={closeEditModal}
                    onSaved={reload}
                />
            )}
        </div>
    );
}

export default RateListPage;
